export const COMMAND_MACRO_SCHEMA_VERSION = 1
export const MAX_COMMAND_MACRO_STEPS = 32
export const COMMAND_MACRO_SOURCE = 'macro'

const COMMAND_ID_RE = /^[a-z0-9][a-z0-9._:-]{0,95}$/
const MACRO_ID_RE = /^[a-z0-9][a-z0-9._:-]{0,63}$/
const PROMPT_INPUT_KINDS = new Set(['text', 'number', 'symbol', 'choice'])
const MACRO_KEYS = new Set(['id', 'name', 'schemaVersion', 'enabled', 'steps'])
const COMMAND_STEP_KEYS = new Set(['type', 'commandId'])
const PROMPT_STEP_KEYS = new Set(['type', 'promptId', 'metadata'])
const PROMPT_METADATA_KEYS = new Set(['label', 'inputKind', 'required'])

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function hasOnlyKeys(value, allowedKeys) {
  return Object.keys(value).every(key => allowedKeys.has(key))
}

function isSafeLabel(value) {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 80 &&
    value.trim() === value &&
    !/[\u0000-\u001f\u007f]/.test(value)
}

export function getRegisteredDescriptor(registry, id) {
  if (typeof id !== 'string' || !COMMAND_ID_RE.test(id)) return null
  if (registry instanceof Map) return registry.get(id) || null
  if (registry instanceof Set) return registry.has(id) ? { id } : null
  if (Array.isArray(registry)) {
    const found = registry.find(entry => (typeof entry === 'string' ? entry === id : entry?.id === id))
    return typeof found === 'string' ? { id: found } : (found || null)
  }
  if (isPlainObject(registry)) {
    const found = registry[id]
    if (found) return typeof found === 'string' ? { id: found } : found
  }
  return null
}

export function hasRegisteredId(registry, id) {
  return getRegisteredDescriptor(registry, id) !== null
}

export function normalizePromptMetadata(value) {
  if (!isPlainObject(value) || !hasOnlyKeys(value, PROMPT_METADATA_KEYS)) return null
  if (!isSafeLabel(value.label)) return null
  if (!PROMPT_INPUT_KINDS.has(value.inputKind)) return null
  if (typeof value.required !== 'boolean') return null
  return {
    label: value.label,
    inputKind: value.inputKind,
    required: value.required,
  }
}

function normalizeStep(step, { commandRegistry, registeredPromptIds, allowBlockedCommands }) {
  if (!isPlainObject(step) || typeof step.type !== 'string') return null

  if (step.type === 'command') {
    if (!hasOnlyKeys(step, COMMAND_STEP_KEYS)) return null
    const descriptor = getRegisteredDescriptor(commandRegistry, step.commandId)
    if (!descriptor) return null
    if (!allowBlockedCommands && (
      descriptor.risk === 'broker_write' ||
      descriptor.macroPolicy === 'forbidden'
    )) return null
    return { type: 'command', commandId: step.commandId }
  }

  if (step.type === 'prompt') {
    if (!hasOnlyKeys(step, PROMPT_STEP_KEYS)) return null
    if (!hasRegisteredId(registeredPromptIds, step.promptId)) return null
    const metadata = normalizePromptMetadata(step.metadata)
    if (!metadata) return null
    return { type: 'prompt', promptId: step.promptId, metadata }
  }

  return null
}

export function normalizeCommandMacro(value, {
  commandRegistry,
  registeredPromptIds = new Set(),
  allowBlockedCommands = false,
} = {}) {
  if (!isPlainObject(value) || !hasOnlyKeys(value, MACRO_KEYS)) return null
  if (typeof value.id !== 'string' || !MACRO_ID_RE.test(value.id)) return null
  if (!isSafeLabel(value.name)) return null
  if (value.schemaVersion !== COMMAND_MACRO_SCHEMA_VERSION) return null
  if (typeof value.enabled !== 'boolean') return null
  if (!Array.isArray(value.steps) || value.steps.length < 1 || value.steps.length > MAX_COMMAND_MACRO_STEPS) return null

  const steps = value.steps.map(step => normalizeStep(step, {
    commandRegistry,
    registeredPromptIds,
    allowBlockedCommands,
  }))
  if (steps.some(step => step === null)) return null

  return {
    id: value.id,
    name: value.name,
    schemaVersion: COMMAND_MACRO_SCHEMA_VERSION,
    enabled: value.enabled,
    steps,
  }
}

function resultBase(macro, mode) {
  return {
    macroId: macro?.id || null,
    mode,
    executedCommandIds: [],
    completedPromptIds: [],
    plannedCommandIds: [],
    plannedPromptIds: [],
  }
}

function stoppedResult(base, fields) {
  return { ...base, ...fields }
}

async function runCommandMacro({
  macro,
  commandRegistry,
  registeredPromptIds = new Set(),
  invokeCommand,
  requestPrompt,
  signal,
  mode,
}) {
  // Runtime revalidation deliberately accepts persisted/forged blocked
  // command references long enough to return a precise fail-closed reason.
  // Persistence uses the stricter default and refuses to store them.
  const normalized = normalizeCommandMacro(macro, {
    commandRegistry,
    registeredPromptIds,
    allowBlockedCommands: true,
  })
  const base = resultBase(normalized || macro, mode)
  if (!normalized) return stoppedResult(base, { status: 'blocked', reason: 'invalid_macro', stoppedBeforeStep: 0 })
  if (!normalized.enabled) return stoppedResult(base, { status: 'blocked', reason: 'macro_disabled', stoppedBeforeStep: 0 })

  for (let stepIndex = 0; stepIndex < normalized.steps.length; stepIndex += 1) {
    if (signal?.aborted) {
      return stoppedResult(base, { status: 'aborted', reason: 'aborted', stoppedBeforeStep: stepIndex })
    }

    const step = normalized.steps[stepIndex]
    if (step.type === 'prompt') {
      if (mode === 'dry-run') {
        base.plannedPromptIds.push(step.promptId)
        continue
      }
      if (typeof requestPrompt !== 'function') {
        return stoppedResult(base, { status: 'blocked', reason: 'prompt_handler_missing', stoppedBeforeStep: stepIndex })
      }
      try {
        const response = await requestPrompt({
          promptId: step.promptId,
          metadata: { ...step.metadata },
          macroId: normalized.id,
          stepIndex,
        })
        if (step.metadata.required && (response === null || response === undefined || response === '')) {
          return stoppedResult(base, { status: 'blocked', reason: 'prompt_cancelled', stoppedBeforeStep: stepIndex })
        }
        // Prompt responses are intentionally ephemeral: they are neither
        // returned nor passed to later commands, which prevents macros from
        // carrying broker values or synthesizing executable arguments.
        base.completedPromptIds.push(step.promptId)
      } catch {
        return stoppedResult(base, { status: 'blocked', reason: 'prompt_failed', stoppedBeforeStep: stepIndex })
      }
      continue
    }

    const descriptor = getRegisteredDescriptor(commandRegistry, step.commandId)
    if (!descriptor) {
      return stoppedResult(base, { status: 'blocked', reason: 'unregistered_command', stoppedBeforeStep: stepIndex })
    }

    if (descriptor.macroPolicy === 'interaction_barrier') {
      return stoppedResult(base, {
        status: 'interaction_barrier',
        reason: 'interaction_barrier',
        stoppedBeforeStep: stepIndex,
        barrierCommandId: step.commandId,
      })
    }
    if (descriptor.risk === 'broker_write') {
      return stoppedResult(base, { status: 'blocked', reason: 'broker_write_forbidden', stoppedBeforeStep: stepIndex })
    }
    if (descriptor.macroPolicy !== 'allowed') {
      return stoppedResult(base, { status: 'blocked', reason: 'forbidden', stoppedBeforeStep: stepIndex })
    }
    if (!Array.isArray(descriptor.allowedSources) || !descriptor.allowedSources.includes(COMMAND_MACRO_SOURCE)) {
      return stoppedResult(base, { status: 'blocked', reason: 'source_not_allowed', stoppedBeforeStep: stepIndex })
    }

    if (mode === 'dry-run') {
      base.plannedCommandIds.push(step.commandId)
      continue
    }
    if (typeof invokeCommand !== 'function') {
      return stoppedResult(base, { status: 'blocked', reason: 'command_handler_missing', stoppedBeforeStep: stepIndex })
    }

    try {
      const invocation = await invokeCommand({
        commandId: step.commandId,
        source: COMMAND_MACRO_SOURCE,
        macroId: normalized.id,
        stepIndex,
      })
      if (invocation?.interactionBarrier === true) {
        return stoppedResult(base, {
          status: 'interaction_barrier',
          reason: invocation.reason || 'runtime_interaction_barrier',
          stoppedBeforeStep: stepIndex,
          barrierCommandId: step.commandId,
        })
      }
      if (!invocation || invocation.ok !== true) {
        return stoppedResult(base, {
          status: 'failed',
          reason: invocation?.reason || 'command_failed',
          stoppedBeforeStep: stepIndex,
          blockedCommandId: step.commandId,
        })
      }
      base.executedCommandIds.push(step.commandId)
    } catch {
      return stoppedResult(base, { status: 'failed', reason: 'command_failed', stoppedBeforeStep: stepIndex })
    }
  }

  return stoppedResult(base, { status: 'completed' })
}

export function executeCommandMacro(options) {
  return runCommandMacro({ ...options, mode: 'execute' })
}

export function dryRunCommandMacro(options) {
  return runCommandMacro({ ...options, mode: 'dry-run' })
}
